import { downloadZip } from 'client-zip';
import { ensureStreamSupport } from './polyfills';

export interface DownloadableFile {
  url: string;
  filename: string;
  mimeType?: string;
}

export interface DownloadProgress {
  current: number;
  total: number;
  filename?: string;
  /** Percentage of current file downloaded (0-100) */
  percentage?: number;
  /** Bytes loaded for current file */
  bytesLoaded?: number;
  /** Total bytes for current file (if known) */
  bytesTotal?: number;
}

/**
 * Extracts a filename from a URL
 */
export const getFilenameFromUrl = (url: string, fallback?: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop() || '';

    if (filename && filename.includes('.')) {
      return filename;
    }

    // If no extension found, try to get from search params
    const disposition = urlObj.searchParams.get('filename');
    if (disposition) {
      return disposition;
    }

    return fallback || `file-${Date.now()}`;
  } catch {
    return fallback || `file-${Date.now()}`;
  }
};

/**
 * Sanitizes a filename to remove dangerous or problematic characters
 */
const sanitizeFilename = (filename: string): string => {
  // Remove path traversal attempts and problematic characters
  return (
    filename
      .replace(/\.\.\/|\.\.\\/g, '') // Remove ../ and ..\
      .replace(/[/\\?%*:|"<>]/g, '-') // Replace dangerous chars with dash
      .replace(/^\.+|\.+$/g, '') // Remove ALL leading/trailing dots
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim() || 'file'
  ); // Fallback to 'file' if empty
};

/**
 * Ensures all filenames in the array are unique by appending numbers if needed
 * Also sanitizes filenames to prevent path traversal and OS compatibility issues
 */
const ensureUniqueFilenames = (files: DownloadableFile[]): DownloadableFile[] => {
  const seen = new Map<string, number>();

  return files.map((file) => {
    // Sanitize the filename first
    let filename = sanitizeFilename(file.filename);
    const count = seen.get(filename) || 0;

    if (count > 0) {
      const parts = filename.split('.');
      if (parts.length > 1) {
        const extension = parts.pop();
        const name = parts.join('.');
        filename = `${name}-${count}.${extension}`;
      } else {
        filename = `${filename}-${count}`;
      }
    }

    seen.set(filename, count + 1);

    return {
      ...file,
      filename,
    };
  });
};

/**
 * Creates a monitored ReadableStream that tracks download progress
 */
const createMonitoredStream = (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  totalBytes: number,
  currentFileNumber: number,
  totalFiles: number,
  filename: string,
  onProgress?: (progress: DownloadProgress) => void,
): ReadableStream<Uint8Array> => {
  return new ReadableStream({
    async start(controller) {
      let bytesLoaded = 0;

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            controller.close();
            break;
          }

          bytesLoaded += value.length;

          // Calculate percentage only if totalBytes is known and valid
          const percentage =
            totalBytes > 0 ? Math.round((bytesLoaded / totalBytes) * 100) : undefined;

          // Report progress
          onProgress?.({
            current: currentFileNumber,
            total: totalFiles,
            filename,
            percentage,
            bytesLoaded,
            bytesTotal: totalBytes,
          });

          controller.enqueue(value);
        }
      } catch (error) {
        controller.error(error);
        throw error;
      }
    },
  });
};

/**
 * Formats bytes to human-readable size
 */
const formatBytes = (bytes: number | null): string => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  const gb = bytes / (1024 * 1024 * 1024);

  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(2)} KB`;
};

export interface DownloadResult {
  /** Number of files successfully downloaded */
  successCount: number;
  /** Number of files that failed to download */
  failedCount: number;
  /** Filenames of files that failed to download */
  failedFiles: string[];
}

/**
 * Downloads multiple files using client-zip (streaming, memory-efficient)
 * Uses sequential fetching to prevent memory issues with large files
 * @param signal Optional AbortSignal to cancel the download operation
 * @returns Download result with success/failure counts and failed file list
 */
export const downloadFilesWithClientZip = async (
  rawFiles: DownloadableFile[],
  zipFilename: string,
  onProgress?: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<DownloadResult> => {
  // Validate input
  if (rawFiles.length === 0) {
    throw new Error('No files provided for download');
  }

  // Ensure filenames are unique and safe
  const files = ensureUniqueFilenames(rawFiles);

  // Ensure streams are supported (loads polyfill if needed)
  await ensureStreamSupport();

  // Track failures and successes for logging
  const failedFiles: string[] = [];
  let successCount = 0;

  // Generate ZIP using streaming
  onProgress?.({
    current: 0,
    total: files.length,
    filename: 'Starting download...',
    percentage: 0,
  });

  type FetchResult =
    | {
        success: true;
        file: DownloadableFile;
        response: Response;
        totalBytes: number | null;
        currentFileNumber: number;
      }
    | {
        success: false;
        file: DownloadableFile;
        error: unknown;
      };

  /**
   * Fetches a single file and returns response data (without creating monitored stream yet)
   */
  const fetchFile = async (
    file: DownloadableFile,
    currentFileNumber: number,
  ): Promise<FetchResult> => {
    try {
      // Check if operation was aborted
      if (signal?.aborted) {
        throw new Error('Download cancelled');
      }

      const response = await fetch(file.url, {
        mode: 'cors',
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Get file size from headers
      const contentLength = response.headers.get('content-length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : null;

      successCount++;

      // Return fetch result with metadata (defer stream creation)
      return {
        success: true as const,
        file,
        response,
        totalBytes,
        currentFileNumber,
      };
    } catch (error) {
      // Handle abort errors gracefully
      if (error instanceof Error && error.name === 'AbortError') {
        throw error; // Re-throw to stop the entire operation
      }

      console.error(`Failed to download ${file.filename}:`, error);
      failedFiles.push(file.filename);

      // Return failed result
      return {
        success: false as const,
        file,
        error,
      };
    }
  };

  /**
   * Async generator for sequential file processing
   * Processes files one by one to minimize memory usage
   */
  async function* generateFileInputs() {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const currentFileNumber = i + 1;

      // Fetch the file (sequentially)
      const fetchResult = await fetchFile(file, currentFileNumber);

      // Now that we're yielding, create the monitored stream (this is when progress shows)
      if (fetchResult.success) {
        const { file, response, totalBytes, currentFileNumber } = fetchResult;

        // Report initial progress with file size info
        const sizeInfo = totalBytes ? ` (${formatBytes(totalBytes)})` : '';
        onProgress?.({
          current: currentFileNumber,
          total: files.length,
          filename: `${file.filename}${sizeInfo}`,
          percentage: 0,
          bytesLoaded: 0,
          bytesTotal: totalBytes || undefined,
        });

        // Create monitored stream only when being processed
        if (response.body) {
          const reader = response.body.getReader();
          const stream = createMonitoredStream(
            reader,
            totalBytes || 0, // Pass 0 if unknown
            currentFileNumber,
            files.length,
            `${file.filename}${sizeInfo}`,
            onProgress,
          );

          yield {
            name: file.filename,
            lastModified: new Date(),
            input: new Response(stream),
          };
        } else {
          // No body available
          yield {
            name: file.filename,
            lastModified: new Date(),
            input: response,
          };
        }
      } else {
        // Failed file - yield empty placeholder
        yield {
          name: fetchResult.file.filename,
          lastModified: new Date(),
          input: new Response(''),
        };
      }
    }

    // Show finalization message after all files are processed
    onProgress?.({
      current: files.length,
      total: files.length,
      filename: 'Finalizing ZIP archive...',
      percentage: undefined, // Indeterminate during final compression
    });
  }

  try {
    // Start ZIP creation (this will stream files through the generator)
    const zipDownload = downloadZip(generateFileInputs());

    const blob = await zipDownload.blob();

    // Validate that at least some files succeeded before downloading
    if (successCount === 0 && failedFiles.length === files.length) {
      throw new Error(`All ${files.length} files failed to download. Cannot create empty ZIP.`);
    }

    // Final progress update
    onProgress?.({
      current: files.length,
      total: files.length,
      filename: `Complete! Downloading ${zipFilename}...`,
      percentage: 100,
    });

    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = zipFilename;
    document.body.appendChild(a);
    a.click();

    // Delay cleanup — browser reads the blob URL asynchronously after click(),
    // revoking immediately causes the download to silently fail in Opera/Firefox/some Chrome versions
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 60000);

    // Log summary if there were failures
    if (failedFiles.length > 0) {
      console.warn(
        `ZIP created with ${failedFiles.length}/${files.length} failed files:`,
        failedFiles,
      );
    }

    // Return download result
    return {
      successCount,
      failedCount: failedFiles.length,
      failedFiles,
    };
  } catch (error) {
    // Propagate AbortError without wrapping so it can be handled as cancellation
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    console.error('Failed to create ZIP:', error);
    throw new Error(
      `ZIP creation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
};

/**
 * Downloads multiple files and creates a ZIP archive using client-zip (streaming, memory-efficient)
 * @param signal Optional AbortSignal to cancel the download operation
 * @returns Download result with success/failure counts and failed file list
 */
export const downloadFilesAsZip = async (
  files: DownloadableFile[],
  zipFilename: string,
  onProgress?: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<DownloadResult> => {
  return await downloadFilesWithClientZip(files, zipFilename, onProgress, signal);
};
